import { useState } from 'react';
import { Button } from 'src/@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import Iconify from 'src/components/Iconify';
import SelectZui from 'src/components/zui/Select';

// Opções de plataforma disponíveis
const platformOptions = [
    {
        id: 1,
        name: 'WhatsApp',
        description: 'Conecte seu WhatsApp pessoal',
        value: 'whatsapp',
        color: 'green-500',
        icon: 'mage:whatsapp-filled',
        disabled: false
    },
    {
        id: 2,
        name: 'WhatsApp Business Oficial',
        description: 'Conecte sua conta oficial do WhatsApp Business',
        value: 'whatsapp_business',
        color: 'blue-500',
        icon: 'uil:whatsapp-alt',
        disabled: false
    },
    {
        id: 3,
        name: 'Instagram Direct',
        description: 'Conecte o Direct do Instagram',
        value: 'instagram',
        color: 'pink-500',
        icon: 'mdi:instagram',
        disabled: false
    },
    {
        id: 4,
        name: 'Facebook Messenger',
        description: 'Conecte o Messenger da sua Página',
        value: 'messenger',
        color: 'blue-500',
        icon: 'ic:round-facebook',
        disabled: false
    },
    {
        id: 5,
        name: 'Webchat',
        description: 'Em breve',
        value: 'webchat',
        color: 'blue-500',
        icon: 'streamline-plump:web',
        disabled: true
    }
];

// Opções de configuração do canal
const webhookTypeOptions = [
    {
        id: 1,
        name: 'Agente IA v2',
        description: 'Agente simples de IA',
        value: 'laisai_v2',
        icon: 'mdi:robot',
        requiredCredits: true
    },
    {
        id: 2,
        name: 'Direcionamento Direto',
        description: 'Direcionamento direto para atendimento humano ou bot',
        value: 'direct',
        icon: 'mdi:arrow-right-bold',
        requiredCredits: false
    },
    {
        id: 3,
        name: 'DialogFlow',
        description: 'Integração com DialogFlow',
        value: 'dialogflow_basic',
        icon: 'mdi:chat-processing',
        requiredCredits: false,
        requiredFullAcc: true
    },
    {
        id: 4,
        name: 'Customizado',
        description: 'Crie fluxos complexos de atendimento',
        value: 'flowbuilder',
        icon: 'mdi:puzzle',
        requiredCredits: false,
        requiredFullAcc: true
    },
    {
        id: 5,
        name: 'Agente IA (v3)',
        description: 'Crie fluxos complexos de atendimento usando IA e ferramentas',
        value: 'laisai_v3',
        icon: 'mdi:robot-outline',
        requiredCredits: true
    },
    {
        id: 6,
        name: 'Agente IA (v3.1)',
        description: '',
        value: 'lais_agents',
        icon: 'mdi:robot-industrial',
        requiredCredits: true
    },
    {
        id: 7,
        name: 'Api Whatsapp',
        description: 'Integre seu sistema com nossa api',
        value: 'api',
        icon: 'mdi:whatsapp',
        requiredCredits: false,
        requiredFullAcc: true
    }
];

// Componente para item de plataforma
const PlatformItem = ({ item, selectedPlatform, onChange }) => {
    const isSelected = selectedPlatform === item.value;

    return (
        <div
            className={`
                border rounded-lg p-3 cursor-pointer transition-all duration-200 hover:shadow-md
                ${isSelected
                    ? 'border-purple-500 bg-purple-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }
                ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onClick={() => !item.disabled && onChange(item.value)}
        >
            <div className="flex items-center space-x-3">
                <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center transition-colors shrink-0
                    ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}
                `}>
                    <Iconify
                        icon={item.icon}
                        width={20}
                        height={20}
                        className={isSelected ? 'text-purple-600' : 'text-gray-600'}
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 leading-tight">
                        {item.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                        {item.description}
                    </div>
                </div>
                {isSelected && (
                    <div className="shrink-0">
                        <Iconify icon="mdi:check-circle" width={20} height={20} className="text-purple-600" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default function ChannelCreationForm({
    onSubmit,
    onChange,
    canal = {}
}) {
    const [nome, setNome] = useState(canal.nome || '');
    const [platform, setPlatform] = useState(canal.platform || 'whatsapp');
    const [webhookType, setWebhookType] = useState(canal.webhook_type || '');

    const handleNomeChange = (event) => {
        const value = event.target.value;
        setNome(value);
        onChange({ target: { name: 'channelName', value } });
    };

    const handlePlatformChange = (value) => {
        setPlatform(value);
        onChange({ target: 'channelType', value });
    };

    const handleWebhookTypeChange = (value) => {
        setWebhookType(value);
        onChange({ target: 'webhookType', value });
    };

    const handleSubmit = () => {
        if (!nome.trim()) {
            return;
        }

        const channelData = {
            nome: nome.trim(),
            platform,
            webhook_type: webhookType
        };

        onSubmit(channelData);
    };

    const isFormValid = nome.trim() && platform && webhookType;

    return (
        <div className="p-6 h-full">
            <div className="space-y-6">
                {/* Campo Nome */}
                <div className="space-y-2">
                    <Label htmlFor="channelName" className="text-sm font-medium text-gray-700">
                        Nome do Canal
                    </Label>
                    <Input
                        id="channelName"
                        value={nome}
                        onChange={handleNomeChange}
                        placeholder="Digite o nome do canal"
                        className="w-full"
                    />
                </div>

                {/* Seleção de Plataforma */}
                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Plataforma</Label>
                    <div className="grid grid-cols-2 gap-3">
                        {platformOptions.map((item) => (
                            <PlatformItem
                                key={item.id}
                                item={item}
                                selectedPlatform={platform}
                                onChange={handlePlatformChange}
                            />
                        ))}
                    </div>
                </div>

                {/* Configurações específicas do WhatsApp Business */}
                {platform === 'whatsapp_business' && (
                    <Alert className="border-blue-200 bg-blue-50">
                        <Iconify icon="mdi:information" width={16} height={16} className="text-blue-600" />
                        <AlertDescription className="text-blue-800">
                            Para WhatsApp Business Oficial, você precisará configurar o Token de Acesso e o ID do Número após criar o canal.
                        </AlertDescription>
                    </Alert>
                )}




                {/* Seleção de Tipo de Webhook */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Configuração do Canal</Label>
                    <SelectZui
                        value={webhookType}
                        list={webhookTypeOptions}
                        onChange={handleWebhookTypeChange}
                        placeholder="Selecione uma configuração"
                        fullWidth={true}
                        itemSelect={(item) => (
                            <SelectItem key={item.value} value={item.value}>
                                <div className="flex items-center gap-3">
                                    <Iconify icon={item.icon} width={20} height={20} className="text-gray-600" />
                                    <div className="flex flex-col">
                                        <span className="font-semibold">{item.name}</span>
                                        <span className="text-xs text-gray-500">{item.description}</span>
                                    </div>
                                </div>
                            </SelectItem>
                        )}
                    />
                </div>

                {/* Botão de Criação */}
                <div className="pt-6">
                    <Button
                        onClick={handleSubmit}
                        disabled={!isFormValid}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        size="lg"
                    >
                        <Iconify icon="majesticons:save-line" width={20} height={20} className="mr-2" />
                        Criar Canal
                    </Button>
                </div>
            </div>
        </div>
    );
}
